import React, { useState, useCallback } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Button } from "@shadcn-in-nx/ui/button";
import { X as XIcon, Plus, Copy, Check } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import { toast } from "sonner";

function formatPhone(raw) {
    const digits = String(raw || "").replace(/\D/g, "");
    if (!digits) return "";
    if (digits.length === 10) {
        return `+1 (${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith("1")) {
        return `+1 (${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return `+${digits}`;
}

export default function TriggerWhatsappSection({ trigger, workflowId }) {
    const { t } = useTranslation();
    const whatsapp = trigger.whatsappTrigger;
    const [allowedPhones, setAllowedPhones] = useState(whatsapp?.allowedPhones || []);
    const [newPhone, setNewPhone] = useState("");
    const [saving, setSaving] = useState(false);
    const [copied, setCopied] = useState(false);
    const { call: updateTrigger } = useBluelagoon({ method: "updateWorkflowTrigger" });

    const whatsappNumberRaw = whatsapp?.whatsappNumber?.number;
    const whatsappNumberFormatted = formatPhone(whatsappNumberRaw);
    const whatsappNumberE164 = whatsappNumberRaw
        ? `+${String(whatsappNumberRaw).replace(/\D/g, "")}`
        : "";

    const copyNumber = useCallback(() => {
        if (!whatsappNumberE164) return;
        navigator.clipboard.writeText(whatsappNumberE164);
        setCopied(true);
        toast.success(t("chatInput.whatsappNumberCopied"));
        setTimeout(() => setCopied(false), 2000);
    }, [whatsappNumberE164, t]);

    const persist = useCallback(async (phones) => {
        setSaving(true);
        try {
            await updateTrigger({
                workflowId, type: "WHATSAPP", workflowTriggerId: trigger.id,
                whatsappNumberId: whatsapp?.whatsappNumber?.id, config: { allowedPhones: phones },
            });
            toast.success(t("chatInput.whatsappTriggerUpdated"));
        } catch { toast.error(t("chatInput.whatsappTriggerUpdateFailed")); }
        setSaving(false);
    }, [workflowId, trigger.id, whatsapp, updateTrigger, t]);

    const addPhone = useCallback(() => {
        const trimmed = newPhone.trim();
        const cleaned = trimmed === "*" ? "*" : trimmed.replace(/\D/g, "");
        if (cleaned && !allowedPhones.includes(cleaned)) {
            const next = [...allowedPhones, cleaned];
            setAllowedPhones(next);
            setNewPhone("");
            persist(next);
        }
    }, [newPhone, allowedPhones, persist]);

    const removePhone = useCallback((phone) => {
        const next = allowedPhones.filter((p) => p !== phone);
        setAllowedPhones(next);
        persist(next);
    }, [allowedPhones, persist]);

    const handleKeyDown = useCallback((e) => {
        if (e.key === "Enter") { e.preventDefault(); addPhone(); }
    }, [addPhone]);

    return (
        <div className="p-4 space-y-4">
            {whatsappNumberRaw && (
                <div>
                    <Label className="text-xs">{t("chatInput.whatsappNumberLabel")}</Label>
                    <button
                        type="button"
                        onClick={copyNumber}
                        className="mt-1 w-full flex items-center gap-2 bg-gray-50 hover:bg-gray-100 border border-gray-200 rounded-lg px-2 py-1.5 transition-colors group"
                        title={t("chatInput.whatsappClickToCopy", { number: whatsappNumberE164 })}
                    >
                        <span className="text-sm text-gray-700 font-mono flex-1 text-left">
                            {whatsappNumberFormatted}
                        </span>
                        {copied ? (
                            <Check size={14} className="text-green-500 flex-shrink-0" />
                        ) : (
                            <Copy size={14} className="text-gray-400 group-hover:text-gray-600 flex-shrink-0" />
                        )}
                    </button>
                </div>
            )}
            <div>
                <Label className="text-xs">{t("chatInput.allowedPhoneNumbersTitle")}</Label>
                {allowedPhones.length === 0 && (
                    <div className="mt-2 relative rounded-lg p-3 bg-gradient-to-br from-amber-50 to-yellow-50 border border-amber-300 shadow-[0_0_14px_rgba(251,191,36,0.55)] ring-1 ring-amber-300/60 animate-pulse">
                        <p className="text-xs text-amber-900 leading-relaxed">
                            {t("chatInput.whatsappEnterFromNumber")}
                        </p>
                        <p className="text-xs text-amber-800 mt-1 leading-relaxed">
                            <Trans
                                i18nKey="chatInput.whatsappOwnNumberHint"
                                components={{
                                    b: <span className="font-semibold" />,
                                    c: <code className="px-1 py-0.5 bg-amber-100 border border-amber-300 rounded font-mono" />,
                                }}
                            />
                        </p>
                        <p className="text-[11px] text-amber-700 mt-2 leading-snug border-t border-amber-200 pt-2">
                            <Trans
                                i18nKey="chatInput.whatsappStarPrivateNote"
                                components={{
                                    b: <span className="font-semibold" />,
                                    c: <code className="px-1 py-0.5 bg-amber-100 border border-amber-300 rounded font-mono" />,
                                }}
                            />
                        </p>
                    </div>
                )}
                <div className="mt-2 space-y-1">
                    {allowedPhones.map((phone) => (
                        <div key={phone} className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded px-2 py-1">
                            <span className="text-xs font-mono text-gray-700 flex-1">{phone === "*" ? t("chatInput.whatsappAnyoneTag") : phone.replace(/[\s+\-]/g, "")}</span>
                            <button onClick={() => removePhone(phone)} className="p-0.5 hover:bg-gray-200 rounded">
                                <XIcon size={12} className="text-gray-400" />
                            </button>
                        </div>
                    ))}
                </div>
                <div className="flex gap-1 mt-2">
                    <Input
                        className="text-sm flex-1"
                        placeholder={t("chatInput.whatsappPhonePlaceholder")}
                        value={newPhone}
                        onChange={(e) => setNewPhone(e.target.value)}
                        onKeyDown={handleKeyDown}
                    />
                    <Button size="sm" variant="outline" onClick={addPhone}>
                        <Plus size={14} />
                    </Button>
                </div>
            </div>
            {saving && <p className="text-xs text-gray-400 text-center">{t("chatInput.saving")}</p>}
        </div>
    );
}
